/**
 * DriverAnalysisParamCard body.
 *
 * Spec: v2-design/CARD_SHELL_v0_spec.md §5.3 (per-control inline edit, no
 * card-level Edit button).
 *
 * Layout:
 *   Output: <picker over candidates>   (segments indented beneath)
 *   Time range: <Timescale>  <start → end>
 *   From: <from_scenario_name>   ↕
 *   To:   <to_scenario_name>
 *
 * Edit affordances:
 *   - Output: MUI Select over output.candidates. On change, dispatch
 *     edit_field({ output: { selected, candidates } }) immediately —
 *     no Edit/Save mode.
 *   - Time range / Scenario swap: wired in subsequent tasks (#25, #26).
 */
import React, { useCallback, useMemo } from 'react';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';

import { OutputAndSegmentsBlock } from '../shared/OutputAndSegmentsBlock';

import {
  isTimescaleSelectable,
  parseSelectableTimescales,
  TIMESCALE_OPTIONS,
  TimescaleLiteral,
  UNAVAILABLE_TIMESCALE_TOOLTIP,
} from './timescaleOptions';

interface DimensionInstance {
  name: string;
  id: number;
}

interface DimensionSegment {
  dimension_depth: number;
  dimension_name: string;
  instances: DimensionInstance[];
}

interface SelectedOutput {
  name: string;
  id: number;
  selection_rationale?: string | null;
}

interface OutputCandidate {
  name: string;
  id: number;
  similarity?: number | null;
}

interface OutputSelection {
  selected: SelectedOutput;
  candidates: OutputCandidate[];
}

interface DateRange {
  start: string;
  end: string;
}

interface DriverAnalysisParamBodyProps {
  content: Record<string, unknown>;
  onEditField: (field_updates: Record<string, unknown>) => void;
  editsDisabled: boolean;
}

export const DriverAnalysisParamBody: React.FC<
  DriverAnalysisParamBodyProps
> = ({ content, onEditField, editsDisabled }) => {
  const output = content.output as OutputSelection;
  const segments = (content.segments ?? []) as DimensionSegment[];
  const segmentRationale =
    typeof content.segment_rationale === 'string' && content.segment_rationale
      ? content.segment_rationale
      : undefined;

  const timescale = content.timescale ? String(content.timescale) : null;
  const startDate = content.start_date ? String(content.start_date) : '';
  const endDate = content.end_date ? String(content.end_date) : '';
  const availableDateRange =
    content.available_date_range &&
    typeof content.available_date_range === 'object'
      ? (content.available_date_range as DateRange)
      : null;
  const timescaleNote =
    typeof content.timescale_note === 'string' && content.timescale_note
      ? content.timescale_note
      : undefined;
  const selectableTimescales = parseSelectableTimescales(
    content.selectable_timescales
  );

  const fromName = String(content.from_scenario_name ?? '');
  const toName = String(content.to_scenario_name ?? '');
  const fromId =
    typeof content.from_scenario_id === 'number'
      ? content.from_scenario_id
      : null;
  const toId =
    typeof content.to_scenario_id === 'number' ? content.to_scenario_id : null;

  return (
    <Stack spacing={1.5}>
      <OutputAndSegmentsBlock
        output={output}
        segments={segments}
        segmentRationale={segmentRationale}
        outputControl={
          <OutputPicker
            output={output}
            onEditField={onEditField}
            disabled={editsDisabled}
          />
        }
      />

      <TimeRangeControls
        timescale={timescale}
        startDate={startDate}
        endDate={endDate}
        availableDateRange={availableDateRange}
        timescaleNote={timescaleNote}
        selectableTimescales={selectableTimescales}
        onEditField={onEditField}
        disabled={editsDisabled}
      />

      <ScenarioPair
        fromName={fromName}
        toName={toName}
        fromId={fromId}
        toId={toId}
        onEditField={onEditField}
        disabled={editsDisabled}
      />
    </Stack>
  );
};

// ---------------------------------------------------------------------------
// Output picker — MUI Select over output.candidates.
// ---------------------------------------------------------------------------

interface OutputPickerProps {
  output: OutputSelection;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const OutputPicker: React.FC<OutputPickerProps> = ({
  output,
  onEditField,
  disabled,
}) => {
  // Candidates are handler-populated, alphabetized, and de-duped against
  // selected. The selected option must be present in the Select's options
  // list for MUI to render the current value; prepend it.
  const options = useMemo<OutputCandidate[]>(() => {
    const selectedOption: OutputCandidate = {
      name: output.selected.name,
      id: output.selected.id,
    };
    return [selectedOption, ...output.candidates];
  }, [output.selected, output.candidates]);

  const handleChange = useCallback(
    (e: SelectChangeEvent<number>) => {
      const nextId = Number(e.target.value);
      if (!Number.isFinite(nextId) || nextId === output.selected.id) return;
      const pick = options.find((o) => o.id === nextId);
      if (!pick) return;
      // Move the previously-selected option back into candidates so the
      // updated card preserves the picker's option list end-to-end. The
      // handler will re-run its candidate backfill on re-fire, so this
      // local optimistic shape is just for the moment between dispatch
      // and the new card emission.
      const nextCandidates: OutputCandidate[] = output.candidates
        .filter((c) => c.id !== pick.id)
        .concat(
          pick.id === output.selected.id
            ? []
            : [{ name: output.selected.name, id: output.selected.id }]
        )
        .sort((a, b) => a.name.localeCompare(b.name));
      onEditField({
        output: {
          selected: { id: pick.id, name: pick.name },
          candidates: nextCandidates,
        },
      });
    },
    [onEditField, options, output.candidates, output.selected]
  );

  return (
    <Select
      value={output.selected.id}
      onChange={handleChange}
      size="small"
      disabled={disabled}
      variant="outlined"
      sx={{
        minWidth: 240,
        maxWidth: 360,
        fontSize: 13,
        '& .MuiSelect-select': {
          py: 0.5,
        },
      }}
      MenuProps={{
        PaperProps: {
          sx: { maxHeight: 320 },
        },
      }}
    >
      {options.map((opt) => (
        <MenuItem key={opt.id} value={opt.id} sx={{ fontSize: 13 }}>
          {opt.name}
        </MenuItem>
      ))}
    </Select>
  );
};

// ---------------------------------------------------------------------------
// Time range controls — timescale toggle + start/end MUI DatePickers.
// Per v2-design/DRIVER_ANALYSIS_DATES_v0_spec.md §2.
//   Yearly    → views={['year']}, commits as Jan 1 of selected year.
//   Quarterly → views={['year', 'month']}, snaps to quarter-first-day.
//   Monthly   → views={['year', 'month']}, commits as first-of-month.
// Each commit dispatches edit_field with the single changed field.
// Backend re-snaps start/end on a timescale change (no client math).
// ---------------------------------------------------------------------------

interface TimeRangeControlsProps {
  timescale: string | null;
  startDate: string;
  endDate: string;
  availableDateRange: DateRange | null;
  timescaleNote?: string;
  selectableTimescales: TimescaleLiteral[] | null;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

// DatePicker layout + density overrides. Colors are NOT set here — the
// app theme (src/theme.ts) handles MuiPickersYear / MuiPickersMonth /
// MuiPickersDay selected-state recoloring globally. Theme-level overrides
// are reliable across the popper's view-transition remounts (year→month);
// slotProps-based color sx was fragile because the popper unmounts and
// re-renders between views and sx didn't always re-apply to the new
// popper instance.
const DATE_PICKER_SLOT_PROPS = {
  textField: {
    size: 'small' as const,
    sx: {
      width: 150,
      // Match card body's 13px body text. MUI's <TextField size="small">
      // defaults to ~16px which made the pickers visually dominate. Target
      // both the input and the placeholder explicitly so they shrink together.
      '& .MuiInputBase-input': { fontSize: 13 },
      '& input': { fontSize: 13 },
    },
  },
  popper: {
    sx: {
      '& .MuiPickersCalendarHeader-root': {
        paddingLeft: 1.5,
        paddingRight: 1,
        marginTop: 0.5,
        marginBottom: 0.5,
        minHeight: 32,
      },
      '& .MuiPickersCalendarHeader-label': {
        fontSize: 13,
        fontWeight: 600,
      },
      '& .MuiDayCalendar-header': {
        '& .MuiTypography-root': {
          fontSize: 11,
          width: 28,
          height: 28,
        },
      },
      '& .MuiPickersDay-root': {
        width: 28,
        height: 28,
        fontSize: 12,
        margin: '1px',
      },
      '& .MuiPickersYear-yearButton': {
        fontSize: 13,
        height: 32,
      },
      '& .MuiPickersMonth-monthButton': {
        fontSize: 13,
      },
    },
  },
};

const TimeRangeControls: React.FC<TimeRangeControlsProps> = ({
  timescale,
  startDate,
  endDate,
  availableDateRange,
  timescaleNote,
  selectableTimescales,
  onEditField,
  disabled,
}) => {
  const handleTimescaleChange = useCallback(
    (_e: React.MouseEvent<HTMLElement>, next: TimescaleLiteral | null) => {
      if (!next || next === timescale) return;
      onEditField({ timescale: next });
    },
    [onEditField, timescale]
  );

  // Use onAccept (fires only after the picker's FINAL view completes) not
  // onChange (fires on every view-step). With views={['year', 'month']},
  // onChange would dispatch on the intermediate year pick — triggering a
  // network round-trip mid-selection, which closed and re-opened the
  // popper and left it in a stale state. onAccept dispatches once, only
  // after the user has reached the last view and picked a value there.
  const handleStartAccept = useCallback(
    (next: Date | null) => {
      const iso = snapToTimescaleStart(next, timescale);
      if (!iso || iso === startDate) return;
      onEditField({ start_date: iso });
    },
    [onEditField, startDate, timescale]
  );

  const handleEndAccept = useCallback(
    (next: Date | null) => {
      const iso = snapToTimescaleStart(next, timescale);
      if (!iso || iso === endDate) return;
      onEditField({ end_date: iso });
    },
    [onEditField, endDate, timescale]
  );

  const datePickerViews = pickerViewsForTimescale(timescale);
  const datePickerFormat = pickerFormatForTimescale(timescale);
  const minDate = parseIsoDate(availableDateRange?.start ?? '');
  const maxDate = parseIsoDate(availableDateRange?.end ?? '');

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 500,
          color: 'text.secondary',
          width: 120,
          flexShrink: 0,
          lineHeight: 1.5,
          pt: 0.75,
        }}
      >
        Time range
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1}>
          <ToggleButtonGroup
            value={timescale ?? ''}
            exclusive
            onChange={handleTimescaleChange}
            size="small"
            disabled={disabled}
            aria-label="Timescale"
          >
            {TIMESCALE_OPTIONS.map((opt) => {
              // Disabled rather than hidden: a toggle silently losing a
              // button reads as a bug, and the tooltip says why.
              //
              // ToggleButton must stay the DIRECT child of
              // ToggleButtonGroup — the group clones its direct children
              // to inject selected/onChange and the grouped borders, so
              // wrapping the button itself would break selection and
              // clicking. The tooltip therefore anchors to the label
              // inside, with pointerEvents restored because a disabled
              // MUI button sets pointer-events: none on its root.
              const selectable = isTimescaleSelectable(
                opt.value,
                selectableTimescales
              );
              return (
                <ToggleButton
                  key={opt.value}
                  value={opt.value}
                  disabled={disabled || !selectable}
                  sx={{
                    fontSize: 12,
                    textTransform: 'none',
                    px: 1.5,
                    py: 0.25,
                  }}
                >
                  {selectable ? (
                    opt.label
                  ) : (
                    <Tooltip title={UNAVAILABLE_TIMESCALE_TOOLTIP}>
                      <span style={{ pointerEvents: 'auto' }}>{opt.label}</span>
                    </Tooltip>
                  )}
                </ToggleButton>
              );
            })}
          </ToggleButtonGroup>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack direction="row" spacing={1} alignItems="center">
              <DatePicker
                value={parseIsoDate(startDate)}
                onAccept={handleStartAccept}
                views={datePickerViews}
                format={datePickerFormat}
                disabled={disabled || !timescale}
                minDate={minDate ?? undefined}
                maxDate={maxDate ?? undefined}
                slotProps={DATE_PICKER_SLOT_PROPS}
              />
              <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                &rarr;
              </Typography>
              <DatePicker
                value={parseIsoDate(endDate)}
                onAccept={handleEndAccept}
                views={datePickerViews}
                format={datePickerFormat}
                disabled={disabled || !timescale}
                minDate={minDate ?? undefined}
                maxDate={maxDate ?? undefined}
                slotProps={DATE_PICKER_SLOT_PROPS}
              />
            </Stack>
          </LocalizationProvider>

          {timescaleNote ? (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {timescaleNote}
            </Typography>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
};

function parseIsoDate(iso: string): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  // Construct in local time; ISO date-only has no TZ ambiguity for display.
  return new Date(parseInt(y, 10), parseInt(mo, 10) - 1, parseInt(d, 10));
}

function snapToTimescaleStart(
  date: Date | null,
  timescale: string | null
): string | null {
  if (!date || isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = date.getMonth(); // 0-11
  switch (timescale) {
    case 'yearly':
      return formatIso(y, 1, 1);
    case 'quarterly': {
      const qStartMonth = Math.floor(m / 3) * 3 + 1; // 1, 4, 7, 10
      return formatIso(y, qStartMonth, 1);
    }
    case 'monthly':
      return formatIso(y, m + 1, 1);
    default:
      return formatIso(y, m + 1, date.getDate());
  }
}

function formatIso(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function pickerViewsForTimescale(
  timescale: string | null
): ('year' | 'month' | 'day')[] {
  switch (timescale) {
    case 'yearly':
      return ['year'];
    case 'quarterly':
    case 'monthly':
      return ['year', 'month'];
    default:
      return ['year', 'month', 'day'];
  }
}

function pickerFormatForTimescale(timescale: string | null): string {
  switch (timescale) {
    case 'yearly':
      return 'yyyy';
    case 'quarterly':
      return 'QQQ yyyy';
    case 'monthly':
      return 'MMM yyyy';
    default:
      return 'yyyy-MM-dd';
  }
}

// ---------------------------------------------------------------------------
// Scenario pair (display-only for now; swap wired in task #26).
// ---------------------------------------------------------------------------

interface ScenarioPairProps {
  fromName: string;
  toName: string;
  fromId: number | null;
  toId: number | null;
  onEditField: (field_updates: Record<string, unknown>) => void;
  disabled: boolean;
}

const ScenarioPair: React.FC<ScenarioPairProps> = ({
  fromName,
  toName,
  fromId,
  toId,
  onEditField,
  disabled,
}) => {
  const handleSwap = useCallback(() => {
    if (fromId === null || toId === null) return;
    onEditField({
      from_scenario_id: toId,
      from_scenario_name: toName,
      to_scenario_id: fromId,
      to_scenario_name: fromName,
    });
  }, [fromId, toId, fromName, toName, onEditField]);

  const swapDisabled =
    disabled || fromId === null || toId === null || fromId === toId;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box
        sx={{
          width: 120,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          From
        </Typography>
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'text.secondary',
            lineHeight: 1.5,
          }}
        >
          To
        </Typography>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={0.25}>
          <Typography
            sx={{ fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}
          >
            {fromName || '—'}
          </Typography>
          <Typography
            sx={{ fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}
          >
            {toName || '—'}
          </Typography>
        </Stack>
      </Box>
      <Tooltip title="Swap From and To">
        <span>
          <IconButton
            size="small"
            onClick={handleSwap}
            disabled={swapDisabled}
            aria-label="Swap From and To scenarios"
          >
            <SwapVertIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};
